import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  canAccessAuditsModule,
  canAccessOrganizationsModule,
  canAccessClientsModule,
  canAccessDevicesModule,
  canAccessUsersModule,
  getRoleLabel,
} from '../../utils/permissions';
import './TopBar.css';

const ICONS = {
  dashboard: (
    <>
      <path d="M3 10.5L12 3l9 7.5" />
      <path d="M5 9.5V20h14V9.5" />
    </>
  ),
  roster: (
    <>
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="10" r="2.5" />
      <path d="M2 20c0-3.3 3.1-6 7-6" />
      <path d="M14 20c0-2.5 2.4-4.5 5.5-4.5" />
    </>
  ),
  clients: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <circle cx="12" cy="13" r="2.5" />
      <path d="M8 7V5h8v2" />
    </>
  ),
  audits: (
    <>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
      <path d="M8 14h2v2H8z" />
    </>
  ),
  reports: (
    <>
      <path d="M6 3h9l4 4v14H6z" />
      <path d="M15 3v4h4" />
      <path d="M9 17v-3M12 17v-6M15 17v-4" />
    </>
  ),
  organizations: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a3 3 0 0 1 6 0v2" />
      <path d="M3 12h18" />
    </>
  ),
  outlets: (
    <>
      <rect x="3" y="4" width="18" height="7" rx="2" />
      <rect x="3" y="13" width="8" height="7" rx="2" />
      <rect x="13" y="13" width="8" height="7" rx="2" />
    </>
  ),
  devices: (
    <>
      <rect x="7" y="2" width="10" height="20" rx="2" />
      <path d="M11 18h2" />
    </>
  ),
  logout: (
    <>
      <path d="M10 17l5-5-5-5" />
      <path d="M15 12H3" />
      <path d="M19 4v16" />
    </>
  ),
};

function Icon({ name }) {
  return (
    <svg className="topbar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      {ICONS[name]}
    </svg>
  );
}

/** Top navigation bar of the admin panel (replaces the left sidebar). */
function TopBar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const userRef = useRef(null);

  const links = [
    { to: '/dashboard', label: 'Dashboard', icon: 'dashboard', show: true },
    { to: '/roster', label: 'Auditor Roster', icon: 'roster', show: canAccessUsersModule(user) },
    {
      to: '/clients',
      label: 'Clients & Stores',
      icon: 'clients',
      show: canAccessClientsModule(user) || canAccessOrganizationsModule(user),
    },
    { to: '/audits', label: 'Schedules', icon: 'audits', show: canAccessAuditsModule(user) },
    { to: '/reports', label: 'Reports', icon: 'reports', show: canAccessAuditsModule(user) },
    { to: '/devices', label: 'Devices', icon: 'devices', show: canAccessDevicesModule(user) },
  ].filter((link) => link.show);

  // Close the menus after navigating.
  useEffect(() => {
    setMenuOpen(false);
    setUserOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!userOpen) return undefined;
    const close = (event) => {
      if (userRef.current && !userRef.current.contains(event.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [userOpen]);

  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();

  return (
    <header className={`topbar ${menuOpen ? 'topbar--open' : ''}`}>
      <div className="topbar__inner">
        <div className="topbar__brand">
          <span className="topbar__brand-badge">IA</span>
          <div>
            <strong>Inventory Audit</strong>
            <small>Admin Panel</small>
          </div>
        </div>

        <button
          type="button"
          className="topbar__menu-btn"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span />
          <span />
          <span />
        </button>

        <nav className="topbar__nav" aria-label="Main">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) => `topbar__link ${isActive ? 'topbar__link--active' : ''}`}
            >
              <Icon name={link.icon} />
              <span>{link.label}</span>
            </NavLink>
          ))}
        </nav>

        {user ? (
          <div className="topbar__user" ref={userRef}>
            <button
              type="button"
              className="topbar__user-btn"
              aria-haspopup="menu"
              aria-expanded={userOpen}
              onClick={() => setUserOpen((open) => !open)}
            >
              <span className="topbar__avatar">{initial}</span>
              <span className="topbar__user-info">
                <strong>{user.name || 'Admin'}</strong>
                <small>{getRoleLabel(user)}</small>
              </span>
            </button>
            {userOpen ? (
              <div className="topbar__user-menu" role="menu">
                <p>
                  <strong>{user.name || 'Admin'}</strong>
                  <span>{user.email}</span>
                  <span>{getRoleLabel(user)}</span>
                </p>
                <button type="button" role="menuitem" onClick={logout}>
                  <Icon name="logout" />
                  Sign out
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}

export default TopBar;
